import { useState, useRef, useEffect, useCallback } from 'react';
import { Photo } from '../types';
import { useSignedUrl } from '../hooks/useSignedUrl';
import './ThumbnailCard.css';

interface ThumbnailCardProps {
  photo: Photo;
  onClick: () => void;
  onError: () => void;
  onRetry: () => void;
  hasFailed: boolean;
}

const ThumbnailCard = ({ photo, onClick, onError, onRetry, hasFailed }: ThumbnailCardProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Get signed URL for thumbnail with fallback to original URL
  const { url: signedThumbnailUrl, loading: urlLoading } = useSignedUrl(
    photo.s3Key,
    'thumbnail',
    { 
      enabled: isVisible,
      fallbackUrl: photo.thumbnailUrl 
    }
  );

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Handle image loading
  useEffect(() => {
    if (isVisible && !isLoaded && !hasFailed && !isLoading && signedThumbnailUrl) {
      setIsLoading(true);
    }
  }, [isVisible, isLoaded, hasFailed, isLoading, signedThumbnailUrl]);

  const handleImageLoad = useCallback(() => {
    setIsLoaded(true);
    setIsLoading(false);
  }, []);

  const handleImageError = useCallback(() => {
    setIsLoading(false);
    onError();
  }, [onError]);

  const handleRetry = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLoading(true);
    onRetry();
  }, [onRetry]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  }, [onClick]);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'Unknown date';
    }
  };

  const getDisplayDate = () => {
    // Prefer EXIF date (when photo was taken) over upload date
    if (photo.metadata.exifDate) {
      return formatDate(photo.metadata.exifDate);
    }
    // Fall back to upload date
    return formatDate(photo.createdAt);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <div
      ref={cardRef}
      className={`thumbnail-card ${isLoaded ? 'loaded' : ''} ${hasFailed ? 'failed' : ''}`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View photo ${photo.filename}`}
    >
      <div className="thumbnail-image-container">
        {isVisible && (
          <>
            {(isLoading || urlLoading) && !hasFailed && (
              <div className="thumbnail-loading">
                <div className="loading-spinner small"></div>
              </div>
            )}
            
            {hasFailed ? (
              <div className="thumbnail-error">
                <div className="error-icon">⚠️</div>
                <p>Failed to load</p>
                <button
                  className="retry-button"
                  onClick={handleRetry}
                  aria-label="Retry loading image"
                >
                  Retry
                </button>
              </div>
            ) : signedThumbnailUrl ? (
              <img
                ref={imgRef}
                src={signedThumbnailUrl}
                alt={photo.filename}
                className={`thumbnail-image ${isLoaded ? 'loaded' : ''}`}
                onLoad={handleImageLoad}
                onError={handleImageError}
                loading="lazy"
                decoding="async"
              />
            ) : null}
          </>
        )}
        
        {!isVisible && (
          <div className="thumbnail-placeholder">
            <div className="placeholder-icon">📷</div>
          </div>
        )}

        {/* Overlay with photo info */}
        <div className="thumbnail-overlay">
          <div className="thumbnail-info">
            <h4 className="thumbnail-title" title={photo.metadata.exifDate ? `Photo taken: ${getDisplayDate()}` : `Uploaded: ${getDisplayDate()}`}>
              {photo.metadata.exifDate ? `Photo taken: ${getDisplayDate()}` : `Uploaded: ${getDisplayDate()}`}
            </h4>
            <div className="thumbnail-meta">
              <span className="thumbnail-filename" title={photo.filename}>
                {photo.filename}
              </span>
              <span className="thumbnail-size">
                {photo.metadata.width} × {photo.metadata.height}
              </span>
              <span className="thumbnail-filesize">
                {formatFileSize(photo.metadata.fileSize)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThumbnailCard;